import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  type Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, Divider, Group, LoadingOverlay, Modal, ScrollArea, Stack, Tooltip } from '@mantine/core';
import { IconEye, IconEyeOff, IconGripVertical, IconTable } from '@tabler/icons-react';
import { ColDef, CsvExportParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import Button from 'components/Button';
import P2Medium from 'components/typography/P2Medium';
import P2Regular from 'components/typography/P2Regular';
import { isFunction, noop } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The saved selection and order of columns in a table export.
 */
export interface DataTableExportConfig {
  columnOrder: string[];
  hideUnselectedColumns: boolean;
  selectedColumns: string[];
}

/**
 * The parameters of data export.
 */
export interface DataTableExport<TData> {
  modalTitle: string;
  columnKeys: string[];
  initialSelectedColumns?: string[] | ((data: TData[]) => string[]);
  alwaysAvailableColumns?: string[];
  /** Maps visible compound columns to their export fields, including one-to-many pairs. */
  columnReplacements?: Record<string, string[]>;
  ready?: boolean;
  error?: unknown;
  fileName: string;
}

/**
 * Parameters of the ExportModal component.
 */
export interface ExportModalProps<TData> {
  agGridRef: React.RefObject<AgGridReact<TData>>;
  columns: ColDef[];
  dataExport: DataTableExport<TData>;
  exportConfig?: DataTableExportConfig;
  visibleColumnKeys?: string[];
  opened: boolean;
  onClose: () => void;
  onExport: (csvExportParams: CsvExportParams) => void | Promise<void>;
  onExportConfigChange?: (config: DataTableExportConfig | undefined) => void;
}

interface SortableExportColumnProps {
  columnKey: string;
  label: string;
  selected: boolean;
  onToggle: () => void;
}

interface SortableExportColumnsProps {
  columnMap: Record<string, ColDef>;
  columnOrder: string[];
  onDragEnd: (event: DragEndEvent) => void;
  onToggle: (columnKey: string) => void;
  selectedSet: Set<string>;
}

/** Keeps sortable export rows on their vertical track. */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * A selectable export column with an accessible drag handle.
 */
function SortableExportColumn({ columnKey, label, selected, onToggle }: SortableExportColumnProps) {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({ id: columnKey });

  return (
    <div
      ref={setNodeRef}
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        justifyContent: 'space-between',
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      data-export-column={columnKey}
    >
      <Checkbox size="md" label={label} checked={selected} onChange={onToggle} />
      <Tooltip label={`Presunúť stĺpec ${label}`} openDelay={300} withArrow={false}>
        <button
          type="button"
          aria-label={`Presunúť stĺpec ${label}`}
          style={{
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            cursor: 'grab',
            display: 'flex',
            padding: 2,
            touchAction: 'none',
          }}
          {...attributes}
          {...listeners}
        >
          <IconGripVertical size={20} />
        </button>
      </Tooltip>
    </div>
  );
}

/**
 * The drag-and-drop layer for export columns.
 */
function SortableExportColumns({
  columnMap,
  columnOrder,
  onDragEnd,
  onToggle,
  selectedSet,
}: SortableExportColumnsProps) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
        {columnOrder.map((key) => (
          <SortableExportColumn
            key={key}
            columnKey={key}
            label={columnMap[key]?.headerName ?? key}
            selected={selectedSet.has(key)}
            onToggle={onToggle.bind(undefined, key)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

/** Expands a visible or former export column into its current export-only columns. */
export function expandExportColumns(columnIds: string[], replacements: Record<string, string[]> = {}) {
  const seen = new Set<string>();

  return columnIds.flatMap((columnId) =>
    (replacements[columnId] ?? [columnId]).flatMap((replacement) => {
      if (seen.has(replacement)) return [];
      seen.add(replacement);
      return replacement;
    })
  );
}

/** Reconciles saved export keys with the currently available export columns. */
function reconcileExportConfig(
  config: DataTableExportConfig,
  availableColumns: string[],
  replacements: Record<string, string[]> = {}
): DataTableExportConfig {
  const availableColumnSet = new Set(availableColumns);
  const knownColumnSet = new Set<string>();
  const knownColumnOrder = expandExportColumns(config.columnOrder, replacements).filter((column) => {
    if (!availableColumnSet.has(column) || knownColumnSet.has(column)) {
      return false;
    }

    knownColumnSet.add(column);
    return true;
  });
  const columnOrder = [...knownColumnOrder, ...availableColumns.filter((column) => !knownColumnSet.has(column))];
  const selectedColumnSet = new Set(
    expandExportColumns(config.selectedColumns, replacements).filter((column) => availableColumnSet.has(column))
  );

  return {
    columnOrder,
    hideUnselectedColumns: config.hideUnselectedColumns ?? false,
    selectedColumns: columnOrder.filter((column) => selectedColumnSet.has(column)),
  };
}

/**
 * Modal which allows the user to export the data.
 */
export default function ExportModal<TData>({
  agGridRef,
  columns,
  dataExport,
  exportConfig,
  visibleColumnKeys,
  opened,
  onClose,
  onExport,
  onExportConfigChange = noop,
}: ExportModalProps<TData>) {
  const [loading, setLoading] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [hideUnselectedColumns, setHideUnselectedColumns] = useState(false);
  const wasOpenedRef = useRef(false);

  const selectedSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);
  const availableColumnOrder = useMemo(() => {
    const availableColumnSet = new Set(dataExport.columnKeys);
    const orderedColumnSet = new Set<string>();
    const orderedColumns = columns.flatMap(({ field }) => {
      if (!field || !availableColumnSet.has(field) || orderedColumnSet.has(field)) {
        return [];
      }

      orderedColumnSet.add(field);
      return field;
    });

    return [...orderedColumns, ...dataExport.columnKeys.filter((column) => !orderedColumnSet.has(column))];
  }, [columns, dataExport.columnKeys]);
  const displayedColumnOrder = useMemo(
    () => (hideUnselectedColumns ? columnOrder.filter((column) => selectedSet.has(column)) : columnOrder),
    [columnOrder, hideUnselectedColumns, selectedSet]
  );

  const columnMap = useMemo(() => {
    const mappedColumns: Record<string, ColDef> = {};

    columns.forEach((column) => {
      mappedColumns[column.field ?? ''] = column;
    });

    return mappedColumns;
  }, [columns]);

  const getDefaultExportConfig = useCallback((): DataTableExportConfig => {
    const rowCount = agGridRef.current?.api.getModel().getRowCount() ?? 0;

    const data = Array.from(
      { length: rowCount },
      (_, i) => agGridRef.current?.api.getDisplayedRowAtIndex(i)?.data as TData
    ).filter((item) => item !== undefined) as TData[];

    const initialSelectedColumns = isFunction(dataExport.initialSelectedColumns)
      ? dataExport.initialSelectedColumns(data)
      : dataExport.initialSelectedColumns ?? availableColumnOrder;
    const visibleColumnSet = new Set(
      expandExportColumns(
        [...(visibleColumnKeys ?? availableColumnOrder), ...(dataExport.alwaysAvailableColumns ?? [])],
        dataExport.columnReplacements
      )
    );
    const selectedColumnSet = new Set(
      expandExportColumns(initialSelectedColumns, dataExport.columnReplacements).filter((column) =>
        visibleColumnSet.has(column)
      )
    );

    return {
      columnOrder: availableColumnOrder,
      hideUnselectedColumns: false,
      selectedColumns: availableColumnOrder.filter((column) => selectedColumnSet.has(column)),
    };
  }, [agGridRef, availableColumnOrder, dataExport, visibleColumnKeys]);

  const updateConfig = useCallback(
    (nextColumnOrder: string[], nextSelectedColumns: string[], nextHideUnselectedColumns = hideUnselectedColumns) => {
      const nextSelectedColumnSet = new Set(nextSelectedColumns);
      const orderedSelectedColumns = nextColumnOrder.filter((column) => nextSelectedColumnSet.has(column));

      setColumnOrder(nextColumnOrder);
      setSelectedColumns(orderedSelectedColumns);
      setHideUnselectedColumns(nextHideUnselectedColumns);
      onExportConfigChange({
        columnOrder: nextColumnOrder,
        hideUnselectedColumns: nextHideUnselectedColumns,
        selectedColumns: orderedSelectedColumns,
      });
    },
    [hideUnselectedColumns, onExportConfigChange]
  );

  const toggleColumn = useCallback(
    (column: string) => {
      const nextSelectedColumns = selectedSet.has(column)
        ? selectedColumns.filter((key) => key !== column)
        : [...selectedColumns, column];

      updateConfig(columnOrder, nextSelectedColumns);
    },
    [columnOrder, selectedColumns, selectedSet, updateConfig]
  );

  const toggleAllColumns = useCallback(() => {
    updateConfig(columnOrder, selectedColumns.length === columnOrder.length ? [] : columnOrder);
  }, [columnOrder, selectedColumns.length, updateConfig]);

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = displayedColumnOrder.indexOf(String(active.id));
      const newIndex = displayedColumnOrder.indexOf(String(over.id));

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const nextDisplayedColumnOrder = arrayMove(displayedColumnOrder, oldIndex, newIndex);
      if (!hideUnselectedColumns) {
        updateConfig(nextDisplayedColumnOrder, selectedColumns);
        return;
      }

      let displayedIndex = 0;
      const nextColumnOrder = columnOrder.map((column) =>
        selectedSet.has(column) ? nextDisplayedColumnOrder[displayedIndex++] : column
      );
      updateConfig(nextColumnOrder, selectedColumns);
    },
    [columnOrder, displayedColumnOrder, hideUnselectedColumns, selectedColumns, selectedSet, updateConfig]
  );

  const handleApplyTableColumns = useCallback(() => {
    const availableColumnSet = new Set(columnOrder);
    const visibleColumns = expandExportColumns(
      visibleColumnKeys ?? availableColumnOrder,
      dataExport.columnReplacements
    ).filter((column) => availableColumnSet.has(column));
    const visibleColumnSet = new Set(visibleColumns);

    updateConfig([...visibleColumns, ...columnOrder.filter((column) => !visibleColumnSet.has(column))], visibleColumns);
  }, [availableColumnOrder, columnOrder, dataExport.columnReplacements, updateConfig, visibleColumnKeys]);

  const handleExport = useCallback(async () => {
    await onExport({
      fileName: dataExport.fileName,
      columnKeys: selectedColumns,
    });

    onClose();
  }, [onExport, onClose, selectedColumns, dataExport.fileName]);

  const handleExportWithLoading = useCallback(
    () =>
      setLoading((loading) => {
        if (!loading) {
          handleExport().then(() => {
            setLoading(false);
          });
        }

        return true;
      }),
    [handleExport]
  );

  useEffect(() => {
    const isOpening = opened && !wasOpenedRef.current;
    wasOpenedRef.current = opened;

    if (isOpening) {
      const nextConfig = exportConfig
        ? reconcileExportConfig(exportConfig, availableColumnOrder, dataExport.columnReplacements)
        : getDefaultExportConfig();

      setColumnOrder(nextConfig.columnOrder);
      setSelectedColumns(nextConfig.selectedColumns);
      setHideUnselectedColumns(nextConfig.hideUnselectedColumns);
    }
  }, [opened, exportConfig, availableColumnOrder, dataExport.columnReplacements, getDefaultExportConfig]);

  const allSelected = columnOrder.length > 0 && selectedColumns.length === columnOrder.length;
  const noneSelected = selectedColumns.length === 0;
  const subsetSelected = selectedColumns.length > 0 && !allSelected;

  return (
    <Modal
      centered
      size={540}
      opened={opened}
      onClose={onClose}
      title={dataExport.modalTitle}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <LoadingOverlay visible={loading} opacity={1} />
      <Group position="apart" pt={4} pb={12}>
        <Checkbox
          size="md"
          checked={allSelected}
          indeterminate={subsetSelected}
          onChange={toggleAllColumns}
          label={<P2Medium>{allSelected ? 'Zrušiť výber' : 'Vybrať všetky stĺpce'}</P2Medium>}
          ml={8}
        />
        <P2Regular>
          Vybrané: {selectedColumns.length} / {columnOrder.length}
        </P2Regular>
      </Group>
      <Divider color="gray.0" className="shadow-sm" />
      <ScrollArea h={500}>
        <Stack spacing={8} px={8} pt={12}>
          <SortableExportColumns
            columnMap={columnMap}
            columnOrder={displayedColumnOrder}
            onDragEnd={handleDragEnd}
            onToggle={toggleColumn}
            selectedSet={selectedSet}
          />
        </Stack>
      </ScrollArea>
      <Group position="apart" spacing={16} pt={16}>
        <Group spacing={8} noWrap>
          <Button
            variant="subtle-gray"
            aria-pressed={hideUnselectedColumns}
            onClick={() => updateConfig(columnOrder, selectedColumns, !hideUnselectedColumns)}
            leftIcon={hideUnselectedColumns ? <IconEye size={20} /> : <IconEyeOff size={20} />}
            px={8}
          >
            {hideUnselectedColumns ? 'Zobraziť všetky' : 'Skryť nevybrané'}
          </Button>
          <Tooltip label="Použiť viditeľné stĺpce a ich poradie" openDelay={300} withArrow={false}>
            <Button variant="subtle-gray" onClick={handleApplyTableColumns} leftIcon={<IconTable size={20} />} px={8}>
              Podľa tabuľky
            </Button>
          </Tooltip>
        </Group>
        <Group position="right" spacing={16} noWrap>
          <Button variant="primary" disabled={noneSelected} onClick={handleExportWithLoading} data-autofocus>
            Exportovať
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
